import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Row,
  Col,
  Card,
  ListGroup,
  Spinner,
  Alert,
  Form,
  Button,
  Badge,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaComments,
  FaImage,
  FaPaperPlane,
  FaBed,
  FaStore,
  FaTimes,
  FaUserCircle,
} from "react-icons/fa";
import api from "../api";
import { AuthContext } from "../context/AuthContext";

const formatDate = (value) => {
  if (!value) {
    return "";
  }
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return "";
  }
};

const formatLastMessage = (message) => {
  if (!message || !message.content) {
    return "No messages yet";
  }
  if (message.content.length <= 80) {
    return message.content;
  }
  return `${message.content.slice(0, 80)}…`;
};

export default function Chats() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [conversationError, setConversationError] = useState("");
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageError, setMessageError] = useState("");
  const [messageDraft, setMessageDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const viewportRef = useRef(null);
  const fileInputRef = useRef(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState("");

  const userId = user?._id || user?.id || "";
  const apiBase = useMemo(
    () => (api.defaults.baseURL || "").replace(/\/api$/, ""),
    []
  );

  const resolveAttachmentUrl = useCallback(
    (src) => {
      if (!src) {
        return "";
      }
      if (/^(https?:)?\/\//i.test(src) || src.startsWith("data:")) {
        return src;
      }
      const needsSlash = !apiBase.endsWith("/") && !src.startsWith("/");
      return `${apiBase}${needsSlash ? "/" : ""}${src}`;
    },
    [apiBase]
  );

  const loadConversations = useCallback(
    async (preferredId) => {
      if (!userId) {
        setConversations([]);
        setLoadingConversations(false);
        return;
      }
      setLoadingConversations(true);
      setConversationError("");
      setDeleteError("");
      try {
        const res = await api.get("/chats", { params: { userId } });
        const items = res.data?.conversations || [];
        setConversations(items);
        if (items.length === 0) {
          setActiveConversationId(null);
          setSearchParams({});
          return;
        }
        const validPreferred =
          preferredId && items.find((item) => item._id === preferredId)
            ? preferredId
            : items[0]._id;
        setActiveConversationId(validPreferred);
        if (!preferredId || preferredId !== validPreferred) {
          setSearchParams({ conversationId: validPreferred });
        }
      } catch (err) {
        console.error("Chats load conversations error:", err);
        setConversationError(
          err.response?.data?.message || "Unable to load your conversations."
        );
        setConversations([]);
        setActiveConversationId(null);
      } finally {
        setLoadingConversations(false);
      }
    },
    [userId, setSearchParams]
  );

  const loadMessages = useCallback(
    async (conversationId, silent = false) => {
      if (!conversationId || !userId) {
        setMessages([]);
        return;
      }
      if (!silent) {
        setLoadingMessages(true);
        setMessageError("");
      }
      try {
        const res = await api.get(`/chats/${conversationId}/messages`, {
          params: { userId },
        });
        setMessages(res.data?.messages || []);
      } catch (err) {
        console.error("Chats load messages error:", err);
        setMessageError(
          err.response?.data?.message || "Unable to load messages."
        );
        setMessages([]);
      } finally {
        if (!silent) {
          setLoadingMessages(false);
        }
      }
    },
    [userId]
  );

  useEffect(() => {
    loadConversations(searchParams.get("conversationId"));
  }, [loadConversations, searchParams]);

  useEffect(() => {
    const conversationId = searchParams.get("conversationId");
    if (!conversationId) {
      return;
    }
    const exists = conversations.find((item) => item._id === conversationId);
    if (exists) {
      setActiveConversationId(conversationId);
    }
  }, [searchParams, conversations]);

  useEffect(() => {
    if (!activeConversationId) {
      setMessages([]);
      return;
    }
    loadMessages(activeConversationId);
  }, [activeConversationId, loadMessages]);

  useEffect(() => {
    if (!activeConversationId) {
      return undefined;
    }
    const interval = setInterval(() => {
      loadMessages(activeConversationId, true);
    }, 8000);
    return () => clearInterval(interval);
  }, [activeConversationId, loadMessages]);

  useEffect(() => {
    if (!viewportRef.current) {
      return;
    }
    viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!attachmentFile) {
      setAttachmentPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(attachmentFile);
    setAttachmentPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [attachmentFile]);

  useEffect(() => {
    if (!userId) {
      const timer = setTimeout(() => navigate("/login"), 1200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [userId, navigate]);

  const activeConversation = useMemo(
    () => conversations.find((item) => item._id === activeConversationId),
    [conversations, activeConversationId]
  );

  const handleSelectConversation = (conversation) => {
    setActiveConversationId(conversation._id);
    setSearchParams({ conversationId: conversation._id });
    setDeleteError("");
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!activeConversationId || !userId) {
      return;
    }
    const trimmed = messageDraft.trim();
    if (!attachmentFile && !trimmed) {
      return;
    }
    setSending(true);
    setMessageError("");
    try {
      let sent;
      if (attachmentFile) {
        const formData = new FormData();
        formData.append("senderId", userId);
        if (trimmed) {
          formData.append("content", trimmed);
        }
        formData.append("image", attachmentFile);
        const res = await api.post(
          `/chats/${activeConversationId}/messages/attachments`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        sent = res.data?.message;
      } else {
        const res = await api.post(`/chats/${activeConversationId}/messages`, {
          senderId: userId,
          content: trimmed,
        });
        sent = res.data?.message;
      }
      if (sent) {
        setMessages((prev) => [...prev, sent]);
        setConversations((prev) =>
          prev.map((item) =>
            item._id === activeConversationId
              ? {
                  ...item,
                  lastMessage: sent,
                  updatedAt: sent.createdAt,
                }
              : item
          )
        );
      }
      setMessageDraft("");
      setAttachmentFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Chats send message error:", err);
      setMessageError(
        err.response?.data?.message || "Unable to send message at this time."
      );
    } finally {
      setSending(false);
    }
  };

  const handleAttachmentChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      setAttachmentFile(null);
      return;
    }
    if (!file.type || !file.type.startsWith("image/")) {
      setMessageError("Please choose an image file.");
      setAttachmentFile(null);
      event.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessageError("Images must be 5MB or smaller.");
      setAttachmentFile(null);
      event.target.value = "";
      return;
    }
    setMessageError("");
    setAttachmentFile(file);
  };

  const handleRemoveAttachment = () => {
    setAttachmentFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteConversation = async () => {
    if (!activeConversationId || !userId) {
      return;
    }
    const confirmed = window.confirm(
      "Delete this conversation and all messages? This cannot be undone."
    );
    if (!confirmed) {
      return;
    }
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await api.delete(`/chats/${activeConversationId}`, {
        data: { userId },
      });

      const remaining = conversations.filter(
        (conversation) => conversation._id !== activeConversationId
      );
      setConversations(remaining);

      if (remaining.length > 0) {
        const nextId = remaining[0]._id;
        setActiveConversationId(nextId);
        setSearchParams({ conversationId: nextId });
      } else {
        setActiveConversationId(null);
        setSearchParams({});
      }
      setMessages([]);
    } catch (err) {
      console.error("Delete conversation error:", err);
      setDeleteError(
        err.response?.data?.message || "Unable to delete conversation."
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderConversationList = () => {
    if (loadingConversations) {
      return (
        <div className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
        </div>
      );
    }
    if (conversationError) {
      return <Alert variant="danger">{conversationError}</Alert>;
    }
    if (conversations.length === 0) {
      return (
        <div className="py-5 text-center text-muted">
          <FaComments size={36} className="mb-3" />
          <p className="mb-0">
            No conversations yet. Start chatting from a listing page.
          </p>
        </div>
      );
    }
    return (
      <ListGroup variant="flush">
        {conversations.map((conversation) => {
          const isActive = conversation._id === activeConversationId;
          const otherUser = conversation.otherUser || conversation.seller;
          const listingName =
            conversation.item?.name || conversation.room?.title || "Listing";
          const listingLocation = conversation.item
            ? [conversation.item.city, conversation.item.state]
                .filter(Boolean)
                .join(", ")
            : conversation.room
            ? [conversation.room.city, conversation.room.state]
                .filter(Boolean)
                .join(", ")
            : "";
          const ListingIcon = conversation.room ? FaBed : FaStore;
          return (
            <ListGroup.Item
              action
              key={conversation._id}
              active={isActive}
              onClick={() => handleSelectConversation(conversation)}
              className="py-3"
            >
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="fw-semibold d-flex align-items-center gap-2">
                    <FaUserCircle />
                    <span>{otherUser?.username || "Unnamed user"}</span>
                  </div>
                  <div className="text-muted small mt-1 d-flex align-items-center gap-2">
                    <ListingIcon className="me-1" />
                    <span>{listingName}</span>
                  </div>
                  {listingLocation && (
                    <div className="text-muted small">{listingLocation}</div>
                  )}
                  <div className="text-muted small mt-1">
                    {formatLastMessage(conversation.lastMessage)}
                  </div>
                </div>
                <small className="text-muted ms-2">
                  {formatDate(conversation.updatedAt)}
                </small>
              </div>
            </ListGroup.Item>
          );
        })}
      </ListGroup>
    );
  };

  const renderMessageArea = () => {
    if (!activeConversation) {
      return (
        <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted">
          <FaComments size={48} className="mb-3" />
          <p className="mb-0">Select a conversation to view messages.</p>
        </div>
      );
    }
    const otherUser = activeConversation.otherUser || activeConversation.seller;
    const isRoomConversation =
      activeConversation.contextType === "room" ||
      (!!activeConversation.room && !activeConversation.item);
    const listingName =
      activeConversation.item?.name ||
      activeConversation.room?.title ||
      "Listing";
    const listingLocation = activeConversation.item
      ? [activeConversation.item.city, activeConversation.item.state]
          .filter(Boolean)
          .join(", ")
      : activeConversation.room
      ? [activeConversation.room.city, activeConversation.room.state]
          .filter(Boolean)
          .join(", ")
      : "";
    return (
      <div className="d-flex flex-column h-100">
        <div className="border-bottom pb-3 mb-3">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
            <div>
              <h5 className="mb-1">{otherUser?.username || "Unnamed user"}</h5>
              <div className="text-muted small d-flex align-items-center gap-2">
                {isRoomConversation ? (
                  <FaBed className="me-1" />
                ) : (
                  <FaStore className="me-1" />
                )}
                <span>{listingName}</span>
              </div>
              {listingLocation && (
                <div className="text-muted small">{listingLocation}</div>
              )}
            </div>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleDeleteConversation}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Delete conversation"}
            </Button>
          </div>
        </div>
        <div
          ref={viewportRef}
          className="flex-grow-1 overflow-auto pe-2"
          style={{ maxHeight: "420px" }}
        >
          {deleteError && (
            <Alert variant="danger" className="mb-3">
              {deleteError}
            </Alert>
          )}
          {loadingMessages ? (
            <div className="py-5 text-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : messageError ? (
            <Alert variant="danger">{messageError}</Alert>
          ) : messages.length === 0 ? (
            <div className="text-center text-muted py-5">
              <p className="mb-0">No messages yet. Say hello to get started.</p>
            </div>
          ) : (
            messages.map((message) => {
              const isOwn = message.sender?._id === userId;
              return (
                <div
                  key={message._id}
                  className={`mb-3 d-flex ${
                    isOwn ? "justify-content-end" : "justify-content-start"
                  }`}
                >
                  <div
                    className={`rounded-3 px-3 py-2 ${
                      isOwn
                        ? "bg-primary text-white"
                        : "bg-light border text-dark"
                    }`}
                    style={{ maxWidth: "80%" }}
                  >
                    <div className="small mb-1">
                      {isOwn ? "You" : message.sender?.username || "User"}
                    </div>
                    {message.content && <div>{message.content}</div>}
                    {Array.isArray(message.attachments) &&
                      message.attachments.length > 0 && (
                        <div className="mt-2 d-flex flex-column gap-2">
                          {message.attachments.map((attachment) => {
                            const resolved = resolveAttachmentUrl(attachment);
                            if (!resolved) {
                              return null;
                            }
                            return (
                              <a
                                key={`${message._id}-${attachment}`}
                                href={resolved}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  src={resolved}
                                  alt="Chat attachment"
                                  className="rounded"
                                  style={{
                                    maxWidth: "220px",
                                    maxHeight: "220px",
                                    width: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </a>
                            );
                          })}
                        </div>
                      )}
                    <div className="small mt-1 opacity-75">
                      {formatDate(message.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <Form onSubmit={handleSendMessage} className="mt-3">
          <Form.Group className="d-flex align-items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() =>
                fileInputRef.current && fileInputRef.current.click()
              }
              disabled={sending}
              title="Add image"
            >
              <FaImage />
            </Button>
            <Form.Control
              type="text"
              placeholder="Type your message"
              value={messageDraft}
              onChange={(event) => setMessageDraft(event.target.value)}
              disabled={sending}
            />
            <Button
              type="submit"
              variant="primary"
              disabled={sending || (!messageDraft.trim() && !attachmentFile)}
            >
              {sending ? "Sending..." : <FaPaperPlane />}
            </Button>
            <Form.Control
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="d-none"
              onChange={handleAttachmentChange}
            />
          </Form.Group>
        </Form>
        {attachmentFile && (
          <div className="mt-2 d-flex align-items-center gap-2">
            {attachmentPreview && (
              <img
                src={attachmentPreview}
                alt="Selected upload"
                className="rounded"
                style={{ width: 56, height: 56, objectFit: "cover" }}
              />
            )}
            <div className="small text-muted flex-grow-1">
              {attachmentFile.name}
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleRemoveAttachment}
            >
              <FaTimes />
            </Button>
          </div>
        )}
      </div>
    );
  };

  if (!userId) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <div>Please sign in to view your messages.</div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Row className="g-4">
      <Col lg={4}>
        <Card className="shadow-sm border-0 h-100">
          <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between">
            <span className="fw-semibold d-flex align-items-center gap-2">
              <FaComments />
              Conversations
            </span>
            {conversations.length > 0 && (
              <Badge bg="light" text="dark">
                {conversations.length}
              </Badge>
            )}
          </Card.Header>
          <Card.Body className="p-0">{renderConversationList()}</Card.Body>
        </Card>
      </Col>
      <Col lg={8}>
        <Card className="shadow-sm border-0 h-100">
          <Card.Body>{renderMessageArea()}</Card.Body>
        </Card>
      </Col>
    </Row>
  );
}
